import type { RouteDetail } from '@motogo/shared';

import { getAll, getOne, openDb, runTx, STORE_ROUTES } from './db';

// Offline mirror of the saved-route library (IndexedDB) + retry queue for saves attempted
// offline. Routes are small (one record holds summary + full polyline + plan), so unlike
// rides there's no summary/points split. Signed-in users only — routes are not guest-local
// (a route is recreatable; a ride recording isn't). See the saved-routes plan (M1).

interface LocalRouteRecord extends RouteDetail {
  synced: boolean;
  cachedAt: string; // ISO 8601 — when this mirror copy was written
}

/** All locally-mirrored routes. Never throws — a store hiccup must not break the list. */
export async function getLocalRoutes(): Promise<RouteDetail[]> {
  try {
    if (typeof indexedDB === 'undefined') return [];
    const db = await openDb();
    try {
      return await getAll<LocalRouteRecord>(db, STORE_ROUTES);
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

/** One locally-mirrored route, or null — the offline fallback for the detail page. Never throws. */
export async function getLocalRoute(id: string): Promise<RouteDetail | null> {
  try {
    if (typeof indexedDB === 'undefined') return null;
    const db = await openDb();
    try {
      return (await getOne<LocalRouteRecord>(db, STORE_ROUTES, id)) ?? null;
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

/** Mirrors one route locally. `synced` records whether the server has this copy. */
export async function putLocalRoute(route: RouteDetail, synced: boolean): Promise<void> {
  const db = await openDb();
  try {
    await runTx(db, STORE_ROUTES, 'readwrite', (tx) => {
      tx.objectStore(STORE_ROUTES).put({
        ...route,
        synced,
        cachedAt: new Date().toISOString(),
      } satisfies LocalRouteRecord);
    });
  } finally {
    db.close();
  }
}

/** The not-yet-uploaded routes, for the sync pass (client-GUID saves retry idempotently). */
export async function getUnsyncedRoutes(): Promise<RouteDetail[]> {
  try {
    if (typeof indexedDB === 'undefined') return [];
    const db = await openDb();
    try {
      const records = await getAll<LocalRouteRecord>(db, STORE_ROUTES);
      return records.filter((r) => !r.synced);
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

/** Flags a mirrored route as uploaded (refreshing it with the server's normalised copy). */
export async function markRouteSynced(route: RouteDetail): Promise<void> {
  await putLocalRoute(route, true);
}

export async function deleteLocalRoute(id: string): Promise<void> {
  const db = await openDb();
  try {
    await runTx(db, STORE_ROUTES, 'readwrite', (tx) => {
      tx.objectStore(STORE_ROUTES).delete(id);
    });
  } finally {
    db.close();
  }
}
